import { useState } from 'react';
import { BannerPreview } from './BannerPreview';
import { Alert } from 'components/ui';
import type {
  Announcement,
  AnnouncementInput,
  AnnouncementSaveResponse,
  AnnouncementStyle,
} from 'types/announcements';

const MARKET_CODES = [
  'AT', 'BE', 'CH', 'COM', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'IT', 'NL', 'NO', 'SE',
];

const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Suomi' },
  { code: 'da', label: 'Dansk' },
  { code: 'no', label: 'Norsk' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'nl', label: 'Nederlands' },
];

const STYLES: { value: AnnouncementStyle; label: string; swatch: string }[] = [
  { value: 'neutral', label: 'Neutral', swatch: 'bg-[#edece7]' },
  { value: 'accent', label: 'Accent', swatch: 'bg-[#0f2b3c]' },
  { value: 'info', label: 'Info', swatch: 'bg-[#e6edf1]' },
  { value: 'warning', label: 'Varning', swatch: 'bg-[#f6e3bd]' },
  { value: 'custom', label: 'Egen färg', swatch: '' },
];

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(localDatetime: string): string {
  return new Date(localDatetime).toISOString();
}

export const AnnouncementForm = ({
  existing,
  onSave,
  onRetranslate,
  onDelete,
  onClose,
  saving,
}: {
  existing?: Announcement;
  onSave: (
    input: AnnouncementInput,
    opts?: { retranslate?: boolean },
  ) => Promise<AnnouncementSaveResponse>;
  onRetranslate?: () => void;
  onDelete?: () => void;
  onClose: () => void;
  saving?: boolean;
}) => {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [bannerTextSv, setBannerTextSv] = useState(existing?.bannerTextSv ?? '');
  const [bannerTextByLang, setBannerTextByLang] = useState<Record<string, string>>(
    (existing?.bannerTextByLang as Record<string, string>) ?? {},
  );
  const [linkUrl, setLinkUrl] = useState(existing?.linkUrl ?? '');
  const [linkUrlByMarket, setLinkUrlByMarket] = useState<Record<string, string>>(
    existing?.linkUrlByMarket ?? {},
  );
  const [marketLinksOpen, setMarketLinksOpen] = useState(false);
  const [linkLabelSv, setLinkLabelSv] = useState(existing?.linkLabelSv ?? '');
  const [linkLabelByLang] = useState<Record<string, string>>(
    (existing?.linkLabelByLang as Record<string, string>) ?? {},
  );
  const [style, setStyle] = useState<AnnouncementStyle>(existing?.style ?? 'neutral');
  const [customColor, setCustomColor] = useState(existing?.customColor ?? '#0f2b3c');
  const [markets, setMarkets] = useState<string[]>(existing?.markets ?? []);
  const [startsAt, setStartsAt] = useState(
    existing ? toLocalDatetime(existing.startsAt) : toLocalDatetime(new Date().toISOString()),
  );
  const [endsAt, setEndsAt] = useState(existing?.endsAt ? toLocalDatetime(existing.endsAt) : '');
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [aiFacts, setAiFacts] = useState(existing?.aiFacts ?? '');

  const [translationsOpen, setTranslationsOpen] = useState(false);
  const [previewLang, setPreviewLang] = useState('sv');
  const [lastResponse, setLastResponse] = useState<AnnouncementSaveResponse | null>(null);

  const translationStale = lastResponse?.translation.stale ?? false;
  const failedLanguages = lastResponse?.translation.failedLanguages ?? [];
  const reviewNotes = lastResponse?.review ?? [];

  const toggleMarket = (code: string) => {
    setMarkets((prev) =>
      prev.includes(code) ? prev.filter((m) => m !== code) : [...prev, code],
    );
  };

  const linkMarkets = markets.length > 0 ? markets : MARKET_CODES;
  const marketsOnDefaultLink = linkMarkets.filter((code) => !linkUrlByMarket[code]?.trim());

  const previewText =
    previewLang === 'sv' ? bannerTextSv : (bannerTextByLang[previewLang] ?? '');

  const handleSubmit = async () => {
    const cleanedMarketLinks = Object.fromEntries(
      Object.entries(linkUrlByMarket)
        .map(([code, url]) => [code, url.trim()])
        .filter(([, url]) => url),
    );
    const input: AnnouncementInput = {
      title,
      bannerTextSv,
      bannerTextByLang,
      linkUrl: linkUrl || null,
      linkUrlByMarket: Object.keys(cleanedMarketLinks).length > 0 ? cleanedMarketLinks : null,
      linkLabelSv: linkLabelSv || null,
      linkLabelByLang: Object.keys(linkLabelByLang).length > 0 ? linkLabelByLang : null,
      style,
      customColor: style === 'custom' ? customColor : null,
      markets,
      startsAt: toIso(startsAt),
      endsAt: endsAt ? toIso(endsAt) : null,
      enabled,
      aiFacts: aiFacts || null,
    };
    const response = await onSave(input);
    setLastResponse(response);

    if (response.announcement.bannerTextByLang) {
      setBannerTextByLang(response.announcement.bannerTextByLang as Record<string, string>);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {existing ? 'Redigera kampanj' : 'Ny kampanj'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="space-y-5">
          {/* Titel */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Titel</label>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
            />
          </div>

          {/* Bannertext (sv) */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Bannertext (svenska)
            </label>
            <textarea
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              rows={4}
              value={bannerTextSv}
              onChange={(e) => setBannerTextSv(e.target.value)}
              maxLength={300}
            />
            <div className="mt-1 text-right text-xs text-gray-400">{bannerTextSv.length}/300</div>
          </div>

          {/* Översättningar */}
          <div>
            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:underline"
              onClick={() => setTranslationsOpen(!translationsOpen)}
            >
              {translationsOpen ? '▾' : '▸'} Översättningar
            </button>
            {translationStale && (
              <span className="ml-2 text-xs text-amber-600">
                Översättningarna är äldre än den svenska texten
                {onRetranslate && (
                  <button type="button" className="ml-1 underline" onClick={onRetranslate}>
                    Översätt igen
                  </button>
                )}
              </span>
            )}
            {translationsOpen && (
              <div className="mt-2 space-y-2">
                {LANGUAGES.map(({ code, label }) => (
                  <div key={code}>
                    <label className="mb-0.5 block text-xs text-gray-500">
                      {label}
                      {failedLanguages.includes(code) && (
                        <span className="ml-1 text-red-500">
                          — Översättning misslyckades
                          {onRetranslate && (
                            <button
                              type="button"
                              className="ml-1 underline"
                              onClick={onRetranslate}
                            >
                              Översätt igen
                            </button>
                          )}
                        </span>
                      )}
                    </label>
                    <textarea
                      className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
                      rows={3}
                      value={bannerTextByLang[code] ?? ''}
                      onChange={(e) =>
                        setBannerTextByLang((prev) => ({ ...prev, [code]: e.target.value }))
                      }
                      maxLength={300}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Länk */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Länk-URL</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder="/sv/black-friday"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Länktext (sv)</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                placeholder="Se erbjudandena"
                value={linkLabelSv}
                onChange={(e) => setLinkLabelSv(e.target.value)}
                maxLength={60}
              />
            </div>
          </div>

          {/* Länk per marknad */}
          <div>
            <button
              type="button"
              className="text-sm font-medium text-gray-700"
              onClick={() => setMarketLinksOpen(!marketLinksOpen)}
            >
              {marketLinksOpen ? '▾' : '▸'} Länk per marknad
            </button>
            {linkUrl && marketsOnDefaultLink.length > 0 && (
              <span className="ml-2 text-xs text-amber-600">
                {marketsOnDefaultLink.join(', ')} skickas till Länk-URL ovan
              </span>
            )}
            {marketLinksOpen && (
              <div className="mt-2 space-y-2">
                {linkMarkets.map((code) => (
                  <div key={code}>
                    <label className="mb-0.5 block text-xs text-gray-500">{code}</label>
                    <input
                      className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                      placeholder={linkUrl || 'https://www.careofcarl.de/de/sale/70-sale'}
                      value={linkUrlByMarket[code] ?? ''}
                      onChange={(e) =>
                        setLinkUrlByMarket({ ...linkUrlByMarket, [code]: e.target.value })
                      }
                      maxLength={500}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stil */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Stil</label>
            <div className="flex flex-wrap gap-3">
              {STYLES.map((s) => (
                <label key={s.value} className="flex cursor-pointer items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="style"
                    checked={style === s.value}
                    onChange={() => setStyle(s.value)}
                  />
                  {s.swatch && (
                    <span className={`inline-block h-4 w-4 rounded ${s.swatch}`} />
                  )}
                  {s.label}
                </label>
              ))}
            </div>
            {style === 'custom' && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  className="h-8 w-8 cursor-pointer rounded border"
                />
                <input
                  className="w-24 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder="#rrggbb"
                />
              </div>
            )}
          </div>

          {/* Marknader */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Marknader</label>
            <div className="flex flex-wrap gap-2">
              <label className="flex cursor-pointer items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={markets.length === 0}
                  onChange={() => setMarkets([])}
                />
                Alla
              </label>
              {MARKET_CODES.map((code) => (
                <label key={code} className="flex cursor-pointer items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={markets.includes(code)}
                    onChange={() => toggleMarket(code)}
                  />
                  {code}
                </label>
              ))}
            </div>
          </div>

          {/* Period */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Start</label>
              <input
                type="datetime-local"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Slut (tomt = tills vidare)
              </label>
              <input
                type="datetime-local"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
          </div>

          {/* Av/på */}
          <div className="flex items-center gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Aktiv
            </label>
          </div>

          {/* AI-fakta */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Fakta till AI:n</label>
            <textarea
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
              rows={4}
              value={aiFacts}
              onChange={(e) => {
                if (e.target.value.length <= 6000) setAiFacts(e.target.value);
              }}
              maxLength={6000}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span>
                {aiFacts.length > 2000 && (
                  <span className="text-amber-600">Långa texter hör hemma i Dokument</span>
                )}
              </span>
              <span className="text-gray-400">{aiFacts.length}/6000</span>
            </div>
          </div>

          {/* Checklista */}
          {reviewNotes.length > 0 && (
            <Alert type="info" title="AI-granskning" message={reviewNotes.join(' · ')} />
          )}

          {/* Förhandsvisning */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Förhandsvisning</span>
              <select
                className="rounded border border-gray-300 px-2 py-1 text-xs"
                value={previewLang}
                onChange={(e) => setPreviewLang(e.target.value)}
              >
                <option value="sv">Svenska</option>
                {LANGUAGES.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {previewText ? (
              <BannerPreview
                banner={{
                  id: existing?.id ?? 'preview',
                  text: previewText,
                  style,
                  color: style === 'custom' ? customColor : undefined,
                  link:
                    linkUrl && linkLabelSv ? { url: linkUrl, label: linkLabelSv } : undefined,
                }}
              />
            ) : (
              <div className="rounded border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-400">
                Ingen text för valt språk
              </div>
            )}
          </div>
        </div>

        {/* Knappar */}
        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <div>
            {existing && onDelete && (
              <button
                type="button"
                className="text-sm text-red-600 hover:underline"
                onClick={onDelete}
              >
                Radera
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={onClose}
            >
              Avbryt
            </button>
            <button
              type="button"
              className="rounded bg-gray-950 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              onClick={handleSubmit}
              disabled={!title || !bannerTextSv || !startsAt || saving}
            >
              {saving ? 'Sparar…' : existing ? 'Spara' : 'Skapa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
